import isEqual from "lodash/isEqual";
import { SearchSuggestions } from "@graylog/server-api";
import { normalizeFromSearchBarForBackend } from "views/logic/queries/NormalizeTimeRange";
import { isNoTimeRangeOverride } from "views/typeGuards/timeRange";
import { escape } from "views/logic/queries/QueryHelper";
import {
  getFieldNameForFieldValueInBrackets,
  isCompleteFieldName,
  isTypeString,
  isTypeTerm,
  isKeywordOperator,
  isSpace,
  isTypeNumber,
  removeFinalColon
} from "views/components/searchbar/completions/token-helper";
const SUGGESTIONS_PAGE_SIZE = 50;
const unquote = (s) => s.replace(/^"(.*(?="$))"$/, "$1");
const formatValue = (value, type) => {
  const trimmedValue = value == null ? void 0 : value.trim();
  switch (type) {
    case "constant.numeric":
      return Number(trimmedValue);
    case "string":
      return unquote(trimmedValue);
    case "keyword.operator":
      return "";
    case "paren.lparen":
      return "";
    default:
      return trimmedValue;
  }
};
const completionCaption = (fieldValue, input, isQuoted) => {
  const quotedValue = isQuoted ? fieldValue : escape(fieldValue);
  if (quotedValue.startsWith(String(input))) {
    return quotedValue;
  }
  return `${input} \u2B62 ${fieldValue}`;
};
const getFieldNameAndInput = ({
  tokens,
  currentToken,
  prevToken,
  currentTokenIdx
}) => {
  var _a;
  const nextToken = (_a = tokens[currentTokenIdx + 1]) != null ? _a : null;
  if (isCompleteFieldName(currentToken) && (!nextToken || isSpace(nextToken))) {
    return {
      fieldName: removeFinalColon(currentToken.value),
      input: "",
      isQuoted: false
    };
  }
  if ((isTypeTerm(currentToken) || isTypeString(currentToken) || isKeywordOperator(currentToken) || isTypeNumber(currentToken)) && isCompleteFieldName(prevToken)) {
    return {
      fieldName: removeFinalColon(prevToken.value),
      input: formatValue(currentToken.value, currentToken.type),
      isQuoted: isTypeString(currentToken)
    };
  }
  const fieldNameFromForValueInBrackets = getFieldNameForFieldValueInBrackets(tokens, currentTokenIdx);
  if (fieldNameFromForValueInBrackets && !(isSpace(currentToken) && (isTypeTerm(prevToken) || isTypeNumber(prevToken)))) {
    return {
      fieldName: fieldNameFromForValueInBrackets,
      input: formatValue(currentToken.value, currentToken.type),
      isQuoted: false
    };
  }
  return {};
};
const isEnumerableField = (field) => {
  var _a;
  return (_a = field == null ? void 0 : field.type.isEnumerable()) != null ? _a : false;
};
const formatSuggestion = (value, occurrence, input, isQuoted, title) => ({
  name: value,
  value: isQuoted ? value : escape(value),
  score: occurrence,
  caption: completionCaption(value, input, isQuoted),
  meta: title ? `${title}: ${occurrence} hits` : `${occurrence} hits`
});
class FieldValueCompletion {
  constructor() {
    // eslint-disable-next-line class-methods-use-this
    this.shouldFetchCompletions = (fieldName, fieldTypes) => {
      if (!fieldName) {
        return false;
      }
      const queryField = fieldTypes == null ? void 0 : fieldTypes.query[fieldName];
      if (!queryField || !isEnumerableField(queryField)) {
        const allFieldType = fieldTypes == null ? void 0 : fieldTypes.all[fieldName];
        return isEnumerableField(allFieldType);
      }
      return true;
    };
    this.getCompletions = ({
      tokens,
      currentToken,
      currentTokenIdx,
      prevToken,
      timeRange,
      streams,
      fieldTypes,
      userTimezone
    }) => {
      const { fieldName, input, isQuoted } = getFieldNameAndInput({
        tokens,
        currentToken,
        currentTokenIdx,
        prevToken
      });
      if (!fieldName) {
        return [];
      }
      if (!this.shouldFetchCompletions(fieldName, fieldTypes)) {
        return [];
      }
      if (this.alreadyFetchedAllSuggestions(input, fieldName, streams, timeRange)) {
        const existingSuggestions = this.filterExistingSuggestions(input, isQuoted);
        if (existingSuggestions.length > 0) {
          return existingSuggestions;
        }
      }
      const normalizedTimeRange = !timeRange || isNoTimeRangeOverride(timeRange) ? void 0 : normalizeFromSearchBarForBackend(timeRange, userTimezone);
      return SearchSuggestions.suggestFieldValue({
        field: fieldName,
        input,
        timerange: normalizedTimeRange,
        streams,
        size: SUGGESTIONS_PAGE_SIZE
      }).then(({ suggestions, sum_other_docs_count: furtherSuggestionsCount }) => {
        if (!suggestions) {
          return [];
        }
        this.previousSuggestions = {
          furtherSuggestionsCount,
          streams,
          timeRange,
          fieldName,
          input,
          suggestions
        };
        return suggestions.map(
          ({ value, occurrence, title }) => formatSuggestion(value, occurrence, input, isQuoted, title)
        );
      });
    };
    this.identifierRegexps = [/[a-zA-Z_0-9$\\/.\-\u00A2-\u2000\u2070-\uFFFF]/];
  }
  alreadyFetchedAllSuggestions(input, fieldName, streams, timeRange) {
    if (!this.previousSuggestions) {
      return false;
    }
    const {
      fieldName: prevFieldName,
      streams: prevStreams,
      timeRange: prevTimeRange,
      furtherSuggestionsCount,
      input: prevInput
    } = this.previousSuggestions;
    return String(input).startsWith(String(prevInput)) && prevFieldName === fieldName && isEqual(prevStreams, streams) && isEqual(prevTimeRange, timeRange) && !furtherSuggestionsCount;
  }
  filterExistingSuggestions(input, isQuoted) {
    if (this.previousSuggestions) {
      return this.previousSuggestions.suggestions.filter(({ value }) => (isQuoted ? value : escape(value)).startsWith(String(input))).map(({ value, occurrence, title }) => formatSuggestion(value, occurrence, input, isQuoted, title));
    }
    return [];
  }
}
export default FieldValueCompletion;
